class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary:not(.no-control-menu)').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button:not(.no-control-menu)').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button') || detailsElement.querySelector(':scope > summary'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        this.hasBackButton ? document.body.classList.add('open-submenu-mobile') : '';
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    if(this.dataset.id) {
      document.body.classList.add(`open-drawer-${this.dataset.id}`);
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
    if(this.dataset.id) {
      document.body.classList.remove(`open-drawer-${this.dataset.id}`);
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    this.hasBackButton ? document.body.classList.remove('open-submenu-mobile') : '';
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    closeDetailsInAnimation(detailsElement);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.hasBackButton = true;
  }

  openMenuDrawer(summaryElement) {
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.header = this.header || document.querySelector('.section-header');
    this.headerContent = document.querySelector('.header');
    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.headerContent.getBoundingClientRect().bottom)}px`);
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

class BackButtonMenu extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    this.button.addEventListener('click', this.onButtonClickHandler);
    this.headerDrawer = document.querySelector('header-drawer');
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.onButtonClickHandler);
  }

  onButtonClick() {
    const openingElements = this.headerDrawer.querySelectorAll('.menu-opening');
    if(openingElements.length == 1) {
      this.headerDrawer.querySelector('.header__icon').dispatchEvent(new Event('click'));
    } else {
      const lastElement = openingElements[openingElements.length - 1];
      lastElement.querySelector(':scope > div > div > .menu-drawer__close-button').dispatchEvent(new Event('click'));
    }
  }
}

customElements.define('back-button-menu', BackButtonMenu);

const animateObserver = new IntersectionObserver((entries, observer) => {
  entries.forEach(entry => {
    if(entry.isIntersecting) {
      entry.target.showImages();
      observer.unobserve(entry.target);
    }
  });
}, {threshold: 0.1});

const animateResizeObserver = new ResizeObserver((entries, observer) => {
  if(window.innerWidth >= 750) {
    entries.forEach(entry => {
      if(!entry.target.inited) {
        entry.target.createObserver();
        observer.unobserve(entry.target);
      }
    });
  }
});

class UseAnimate extends HTMLElement {
  constructor() {
    super();
    this.inited = false;
  }

  connectedCallback() {
    if(window.innerWidth >= 750) {
      this.createObserver();
    } else {
      animateResizeObserver.observe(this);
    }
  }

  createObserver() {
    this.inited = true;
    animateObserver.observe(this);
  }

  showImages() {
    this.classList.add('loaded');
  }
}

customElements.define('use-animate', UseAnimate);

class ToastMessageManager extends HTMLElement {
  constructor() {
    super();
  }

  pushMessage(content, type) {
    var toast = document.createElement('toast-message');
    toast.setContent(content, type);
    this.appendChild(toast);
    setTimeout(() => {
      toast.classList.add('open');
    }, 300);
  }
}

customElements.define('toast-message-manager', ToastMessageManager);

class ToastMessage extends HTMLElement
{
  constructor() {
    super();
    this.waitToCloseTimeout = setTimeout(() => {
      this.close();
    }, 3300);
  }

  connectedCallback() {
    this.appendCloseButton();
  }

  setContent(str, type) {
    this.appendChild(document.createElement('div'));
    this.contentWrapper = this.querySelector('div');
    this.contentWrapper.classList.add('alert');
    this.contentWrapper.textContent = str;
    this.contentWrapper.classList.add(`alert--${type}`);
  }
 
  close() {
    this.classList.remove('open');
    setTimeout(() => {
      this.remove();
    }, 300);
  }

  appendCloseButton() {
    const button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.classList.add('toast__button', 'color-foreground');
    const icon = document.querySelector('.icon-close-sample').cloneNode(true);
    icon.classList.remove('hidden');
    button.appendChild(icon);
    this.onButtonClickHandler = this.onButtonClick.bind(this);
    button.addEventListener('click', this.onButtonClickHandler);
    this.contentWrapper.appendChild(button);
  }

  onButtonClick() {
    if(this.waitToCloseTimeout) {
      clearTimeout(this.waitToCloseTimeout);
    }
    this.close();
  }
}

customElements.define('toast-message', ToastMessage);

function pushMessage(content, type) {
  document.querySelector('toast-message-manager').pushMessage(content, type);
}

function pushSuccessMessage(content) {
  pushMessage(content, 'success');
}

function pushErrorMessage(content) {
  pushMessage(content, 'error');
}

(function() {
  const _target_meun_line = document.querySelector("header .header__inline-menu nav")
  if(_target_meun_line && document.documentElement.style.getPropertyValue('--header-menu-height-desktop') === '') {
    document.documentElement.style.setProperty(
      '--header-menu-height-desktop',
      `${Math.floor(_target_meun_line.getBoundingClientRect().height)}px`
    );
  }

  const _header_top = document.querySelector("sticky-header .header-top")
  if(_header_top && document.documentElement.style.getPropertyValue('--header-top-height-desktop') === '') {
    document.documentElement.style.setProperty(
      '--header-top-height-desktop',
      `-${Math.ceil(_header_top.getBoundingClientRect().height)}px`
    );
  }
})();

/**
 * 扩展的系列页面登录检查
 * 限制未登录用户的产品浏览、快速查看和加购功能
 */
class CollectionLoginModal {
  constructor() {
    // 检测预览模式
    this.isPreviewMode = window.location.hostname.includes('myshopify.com') || 
                         window.location.search.includes('preview_theme_id') ||
                         document.querySelector('body').classList.contains('shopify-preview') ||
                         window.Shopify?.designMode;
    
    // 修复登录状态判断逻辑 - 使用更宽松的检测
    const customerData = window.collectionLoginModal?.customer;
    // 检测方法1：检查客户对象是否存在且有ID
    const hasValidCustomer = customerData && 
                            typeof customerData === 'object' && 
                            customerData.id;
    
    // 检测方法2：直接使用liquid的检测结果
    const liquidDetection = window.collectionLoginModal?.isLoggedIn;
    
    // 检测方法3：检查是否有任何客户相关数据
    const hasAnyCustomerData = customerData !== null && customerData !== undefined;
    
    // 使用多种方法的组合检测
    this.isLoggedIn = liquidDetection || hasValidCustomer || hasAnyCustomerData;
    
    console.log('🔍 多重登录检测:');
    console.log('- Liquid检测:', liquidDetection);
    console.log('- 有效客户:', hasValidCustomer);
    console.log('- 有客户数据:', hasAnyCustomerData);
    console.log('- 最终结果:', this.isLoggedIn);
    
    // 预览模式下禁用登录检查
    this.enableLoginCheck = window.collectionLoginModal?.enableLoginCheck === true && !this.isPreviewMode;
    
    console.log('🔧 登录检查功能状态:', this.enableLoginCheck ? '已启用' : '已禁用');
    console.log('🔍 登录状态检测:', this.isLoggedIn ? '已登录' : '未登录');
    console.log('📊 客户数据:', customerData);
    console.log('🎭 预览模式:', this.isPreviewMode ? '是' : '否');
    
    if (this.isPreviewMode) {
      console.log('⚠️ 预览模式下登录检查已自动禁用');
    }
    
    this.init();
  }
  
  init() {
    // 只有启用登录检查时才初始化拦截功能
    if (this.enableLoginCheck) {
      this.interceptProductInteractions();
      this.addVisualIndicators();
    }
  }
  
  addVisualIndicators() {
    // 为已登录用户的产品卡片添加标识，避免显示登录提示
    if (this.isLoggedIn) {
      document.addEventListener('DOMContentLoaded', () => {
        const productCards = document.querySelectorAll('.card-product, .product-card, .grid-product, .product-item');
        productCards.forEach(card => {
          card.classList.add('logged-in-user');
        });
      });
    }
  }
  
  interceptProductInteractions() {
    // 避免重复绑定事件监听器
    if (this.eventListenerBound) {
      return;
    }
    this.eventListenerBound = true;
    
    // 拦截所有产品相关的交互事件
    document.addEventListener('click', (e) => {
      // 检查是否在系列页面
      if (!this.isCollectionPage()) {
        return; // 非系列页面不拦截
      }
      
      // 实时检查登录状态 - 防止状态不同步
      const currentCustomerData = window.collectionLoginModal?.customer;
      const currentLiquidDetection = window.collectionLoginModal?.isLoggedIn;
      const currentHasValidCustomer = currentCustomerData && 
                                     typeof currentCustomerData === 'object' && 
                                     currentCustomerData.id;
      const currentHasAnyData = currentCustomerData !== null && currentCustomerData !== undefined;
      
      const currentIsLoggedIn = currentLiquidDetection || currentHasValidCustomer || currentHasAnyData;
      
      // 如果已经登录，允许所有操作
      if (currentIsLoggedIn) {
        return;
      }
      
      // 未登录用户的拦截逻辑
      const restrictedElement = this.checkRestrictedElement(e.target);
      
      if (restrictedElement) {
        console.log('🚫 拦截未登录用户操作:', restrictedElement.type);
        // 阻止默认行为
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.redirectToLogin(restrictedElement.type);
      }
    }, true); // 使用捕获阶段确保优先执行
  }
  
  checkRestrictedElement(target) {
    // 1. 检查加购按钮 - 更新为实际的选择器
    const addToCartButton = target.closest(`
      .cart-submit-button, 
      .quick-add__submit, 
      [name="add"], 
      .btn-add-to-cart,
      .product-form__cart-submit,
      .shopify-payment-button__button,
      [data-action="add-to-cart"],
      .product-form__buttons button[type="submit"],
      button[type="submit"][name="add"]
    `.replace(/\s+/g, ''));
    
    if (addToCartButton) {
      return { type: 'add-to-cart', element: addToCartButton };
    }
    
    // 2. 检查快速查看按钮 - 更新为实际的选择器
    const quickViewButton = target.closest(`
      .quick-view__submit,
      .quick-button--view,
      quick-view-modal-opener,
      .quick-view-button,
      .quick-view__button,
      [data-action="quick-view"],
      .btn-quick-view,
      .card-product__quick-view,
      .product-card__quick-view,
      .quick-view-trigger,
      [data-modal="#QuickView-Modal"]
    `.replace(/\s+/g, ''));
    
    if (quickViewButton) {
      return { type: 'quick-view', element: quickViewButton };
    }
    
    // 3. 检查产品卡片链接（但排除色卡等小元素）
    const productLink = target.closest('a[href*="/products/"]');
    
    if (productLink) {
      // 排除色卡、按钮等小元素的点击
      const isSmallElement = target.closest(`
        .color-swatch,
        .product-color-swatches,
        .card-product-colors,
        .product-variant-picker,
        .product-options,
        .quick-add__submit,
        .cart-submit-button,
        .quick-view__submit,
        .quick-button--view,
        quick-view-modal-opener,
        button,
        input,
        select,
        .icon,
        .loading-overlay__spinner
      `.replace(/\s+/g, ''));
      
      // 确保是产品卡片主体区域的点击
      const isProductCard = target.closest(`
        .card-wrapper--product,
        .card--product,
        .card-product,
        .product-card,
        .grid-product,
        .product-item,
        .collection-product-card,
        .product-grid-item,
        .grid__item
      `.replace(/\s+/g, ''));
      
      if (!isSmallElement && isProductCard) {
        return { type: 'product-link', element: productLink };
      }
    }
    
    return null;
  }
  
  isCollectionPage() {
    // 检查当前页面是否为系列页面
    return document.body.classList.contains('template-collection') ||
           window.location.pathname.includes('/collections/') ||
           document.querySelector('.main-collection-product-grid-section');
  }
  
  redirectToLogin(actionType = 'add-to-cart') {
    // 根据不同操作类型显示不同的提示信息
    const messages = {
      'add-to-cart': 'Member exclusive flash sale, purchase requires login first.',
      'quick-view': 'Member exclusive flash sale, purchase requires login first.',
      'product-link': 'Member exclusive flash sale, purchase requires login first.'
    };
    
    const message = messages[actionType] || 'Please log in first';
    
    // 显示提示信息（可选）
    if (window.pushErrorMessage) {
      window.pushErrorMessage(message);
    }
    
    // 跳转到登录页面
    const currentUrl = window.location.href;
    const returnUrl = encodeURIComponent(currentUrl);
    
    // 延迟跳转，让用户看到提示信息
    setTimeout(() => {
      window.location.href = `https://torraslife.com/account/login?return_url=${returnUrl}`;
    }, 1000);
  }
  
  // 动态切换登录检查功能
  toggleLoginCheck(enabled) {
    this.enableLoginCheck = enabled;
    
    if (enabled) {
      // 重新初始化拦截功能
      this.interceptProductInteractions();
    }
  }
}

// 初始化系列页面登录检查
if (document.body.classList.contains('template-collection') || window.location.pathname.includes('/collections/')) {
  window.collectionLoginModalInstance = new CollectionLoginModal();
}

// 简单的登录状态测试函数
window.quickLoginTest = function() {
  console.log('🧪 快速登录状态测试');
  console.log('='.repeat(40));
  
  const isPreviewMode = window.location.hostname.includes('myshopify.com') || 
                        window.location.search.includes('preview_theme_id') ||
                        document.querySelector('body').classList.contains('shopify-preview') ||
                        window.Shopify?.designMode;
  
  const customerData = window.collectionLoginModal?.customer;
  const liquidDetection = window.collectionLoginModal?.isLoggedIn;
  const hasValidCustomer = customerData && 
                          typeof customerData === 'object' && 
                          customerData.id;
  const hasAnyData = customerData !== null && customerData !== undefined;
  const finalLoginStatus = liquidDetection || hasValidCustomer || hasAnyData;
  
  console.log('预览模式:', isPreviewMode ? '✅ 是' : '❌ 否');
  console.log('客户数据:', customerData);
  console.log('Liquid检测:', liquidDetection ? '✅ 已登录' : '❌ 未登录');
  console.log('有效客户:', hasValidCustomer ? '✅ 已登录' : '❌ 未登录');
  console.log('有数据:', hasAnyData ? '✅ 有' : '❌ 无');
  console.log('最终状态:', finalLoginStatus ? '✅ 已登录' : '❌ 未登录');
  console.log('启用检查:', window.collectionLoginModal?.enableLoginCheck ? '✅ 已启用' : '❌ 未启用');
  
  if (window.collectionLoginModalInstance) {
    console.log('实例状态:', window.collectionLoginModalInstance.isLoggedIn ? '✅ 已登录' : '❌ 未登录');
    console.log('实例预览模式:', window.collectionLoginModalInstance.isPreviewMode ? '✅ 是' : '❌ 否');
  }
  
  if (isPreviewMode) {
    console.log('💡 预览模式说明: 在预览模式下，customer对象通常为null，这是正常的');
    console.log('💡 登录检查已自动禁用，所有功能应该正常工作');
  }
  
  console.log('='.repeat(40));
  return finalLoginStatus;
};

// 强制设置登录状态的测试函数
window.forceLoginStatus = function(isLoggedIn = true) {
  console.log('🔧 强制设置登录状态:', isLoggedIn ? '已登录' : '未登录');
  
  if (window.collectionLoginModal) {
    window.collectionLoginModal.isLoggedIn = isLoggedIn;
    if (isLoggedIn) {
      // 创建一个模拟的客户对象
      window.collectionLoginModal.customer = {
        id: 123456,
        email: 'test@example.com',
        first_name: 'Test',
        last_name: 'User'
      };
    } else {
      window.collectionLoginModal.customer = null;
    }
  }
  
  if (window.collectionLoginModalInstance) {
    window.collectionLoginModalInstance.isLoggedIn = isLoggedIn;
  }
  
  console.log('✅ 登录状态已强制设置，请测试功能');
  return isLoggedIn;
};

// 扩展的测试函数
window.testExtendedLoginCheck = function() {
  console.log('🧪 测试扩展的登录检查功能...');
  
  const modal = new CollectionLoginModal();
  console.log('登录状态:', modal.isLoggedIn ? '已登录' : '未登录');
  console.log('登录检查开关:', modal.enableLoginCheck ? '已启用' : '已禁用');
  console.log('是否系列页面:', modal.isCollectionPage());
  
  // 测试选择器识别
  console.log('\n=== 测试选择器识别 ===');
  const testSelectors = [
    {
      name: '产品卡片链接',
      selector: '.card-wrapper--product a[href*="/products/"]',
      expectedType: 'product-link'
    },
    {
      name: '快速查看按钮',
      selector: '.quick-view__submit',
      expectedType: 'quick-view'
    },
    {
      name: '快速查看容器',
      selector: 'quick-view-modal-opener',
      expectedType: 'quick-view'
    },
    {
      name: '加购按钮',
      selector: '.quick-add__submit',
      expectedType: 'add-to-cart'
    }
  ];
  
  testSelectors.forEach(test => {
    const elements = document.querySelectorAll(test.selector);
    console.log(`${test.name}: 找到 ${elements.length} 个元素`);
    
    if (elements.length > 0) {
      const firstElement = elements[0];
      const result = modal.checkRestrictedElement(firstElement);
      if (result && result.type === test.expectedType) {
        console.log(`  ✅ 正确识别为 ${result.type}`);
      } else if (result) {
        console.log(`  ⚠️ 识别错误: 期望 ${test.expectedType}, 实际 ${result.type}`);
      } else {
        console.log(`  ❌ 未能识别限制类型`);
      }
    }
  });
  
  // 测试产品卡片的不同区域
  console.log('\n=== 测试产品卡片区域 ===');
  const productCards = document.querySelectorAll('.card-wrapper--product');
  if (productCards.length > 0) {
    const firstCard = productCards[0];
    console.log(`找到 ${productCards.length} 个产品卡片，测试第一个卡片`);
    
    // 测试图片区域
    const productImage = firstCard.querySelector('.media img');
    if (productImage) {
      const result = modal.checkRestrictedElement(productImage);
      console.log('产品图片点击:', result ? `✅ ${result.type}` : '❌ 未检测到');
    }
    
    // 测试标题链接
    const productTitle = firstCard.querySelector('.card__heading a');
    if (productTitle) {
      const result = modal.checkRestrictedElement(productTitle);
      console.log('产品标题点击:', result ? `✅ ${result.type}` : '❌ 未检测到');
    }
    
    // 测试快速查看按钮
    const quickViewBtn = firstCard.querySelector('.quick-view__submit');
    if (quickViewBtn) {
      const result = modal.checkRestrictedElement(quickViewBtn);
      console.log('快速查看按钮:', result ? `✅ ${result.type}` : '❌ 未检测到');
    }
    
    // 测试加购按钮
    const addToCartBtn = firstCard.querySelector('.quick-add__submit');
    if (addToCartBtn) {
      const result = modal.checkRestrictedElement(addToCartBtn);
      console.log('加购按钮:', result ? `✅ ${result.type}` : '❌ 未检测到');
    }
    
    // 测试色卡（应该被排除）
    const colorSwatch = firstCard.querySelector('.color-swatch');
    if (colorSwatch) {
      const result = modal.checkRestrictedElement(colorSwatch);
      console.log('色卡点击:', result ? `⚠️ 意外检测到 ${result.type}` : '✅ 正确排除');
    }
  } else {
    console.log('页面上没有找到产品卡片');
  }
  
  if (modal.enableLoginCheck) {
    if (!modal.isLoggedIn && modal.isCollectionPage()) {
      console.log('\n✅ 功能正常：未登录用户在系列页面会被拦截');
      console.log('  - 限制产品卡片点击跳转');
      console.log('  - 限制快速查看功能');
      console.log('  - 限制加购按钮');
    } else if (modal.isLoggedIn) {
      console.log('\n✅ 功能正常：已登录用户可以正常浏览和购买');
    } else {
      console.log('\nℹ️ 不在系列页面，登录检查不会生效');
    }
  } else {
    console.log('\nℹ️ 登录检查功能已禁用，所有用户可正常浏览和购买');
  }
  
  return {
    isLoggedIn: modal.isLoggedIn,
    enableLoginCheck: modal.enableLoginCheck,
    isCollectionPage: modal.isCollectionPage(),
    restrictedActions: ['product-link', 'quick-view', 'add-to-cart']
  };
};

// 保持向后兼容
window.testSimpleLoginCheck = window.testExtendedLoginCheck;

// 动态控制登录检查功能的全局函数
window.toggleCollectionLoginCheck = function(enabled) {
  if (window.collectionLoginModalInstance) {
    window.collectionLoginModalInstance.toggleLoginCheck(enabled);
  } else {
    console.warn('⚠️ 登录检查实例未找到，请确保在系列页面');
  }
};

// 快速检查当前登录状态的调试函数
window.checkLoginStatus = function() {
  console.log('🔍 当前登录状态检查:');
  console.log('- window.collectionLoginModal:', window.collectionLoginModal);
  
  if (window.collectionLoginModal) {
    const customerData = window.collectionLoginModal.customer;
    const isLoggedIn = customerData && 
                      typeof customerData === 'object' && 
                      customerData.id && 
                      customerData.id !== null;
    
    console.log('- customer对象:', customerData);
    console.log('- isLoggedIn (原始):', window.collectionLoginModal.isLoggedIn);
    console.log('- isLoggedIn (重新计算):', isLoggedIn);
    console.log('- enableLoginCheck:', window.collectionLoginModal.enableLoginCheck);
    
    if (customerData && typeof customerData === 'object') {
      console.log('- 客户ID:', customerData.id);
      console.log('- 客户邮箱:', customerData.email);
      console.log('- 客户姓名:', customerData.first_name, customerData.last_name);
    }
  } else {
    console.log('❌ window.collectionLoginModal 未定义');
  }
  
  if (window.collectionLoginModalInstance) {
    console.log('- 实例登录状态:', window.collectionLoginModalInstance.isLoggedIn);
    console.log('- 实例功能开关:', window.collectionLoginModalInstance.enableLoginCheck);
  } else {
    console.log('❌ window.collectionLoginModalInstance 未定义');
  }
  
  return {
    hasModal: !!window.collectionLoginModal,
    hasInstance: !!window.collectionLoginModalInstance,
    customerData: window.collectionLoginModal?.customer,
    isLoggedIn: window.collectionLoginModal?.customer && 
                typeof window.collectionLoginModal?.customer === 'object' && 
                window.collectionLoginModal?.customer.id && 
                window.collectionLoginModal?.customer.id !== null,
    enableLoginCheck: window.collectionLoginModal?.enableLoginCheck
  };
};

// 测试不同限制类型的函数
window.testLoginRestrictions = function() {
  console.log('🧪 测试登录限制的各种场景...');
  
  const testCases = [
    {
      name: '产品卡片点击',
      selector: '.card-product a[href*="/products/"], .product-card a[href*="/products/"]',
      action: 'product-link'
    },
    {
      name: '快速查看按钮',
      selector: '.quick-view-button, [data-action="quick-view"]',
      action: 'quick-view'
    },
    {
      name: '加购按钮',
      selector: '.quick-add__submit, .cart-submit-button, [name="add"]',
      action: 'add-to-cart'
    }
  ];
  
  testCases.forEach(testCase => {
    const elements = document.querySelectorAll(testCase.selector);
    console.log(`${testCase.name}: 找到 ${elements.length} 个元素`);
    
    if (elements.length > 0) {
      console.log(`  - 选择器: ${testCase.selector}`);
      console.log(`  - 限制类型: ${testCase.action}`);
    }
  });
  
  const modal = window.collectionLoginModalInstance;
  if (modal) {
    console.log('\n当前状态:');
    console.log('- 登录状态:', modal.isLoggedIn ? '已登录' : '未登录');
    console.log('- 登录检查:', modal.enableLoginCheck ? '已启用' : '已禁用');
    console.log('- 系列页面:', modal.isCollectionPage() ? '是' : '否');
    
    if (modal.enableLoginCheck && !modal.isLoggedIn && modal.isCollectionPage()) {
      console.log('\n✅ 限制将生效：未登录用户的操作会被拦截');
    } else {
      console.log('\n❌ 限制不生效：用户可以正常操作');
    }
  }
};
